import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Receipt, Printer, X } from "lucide-react";
import { format } from "date-fns";
import ReceiptPreview from "../components/ReceiptPreview";

export default function TodaysSales() {
  const { getAuthHeader } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  useEffect(() => {
    fetchTodaysSales();
  }, []);

  const fetchTodaysSales = async () => {
    try {
      const response = await axios.get(`${API}/sales/today`, getAuthHeader());
      setSales(response.data);
    } catch (error) {
      toast.error("Failed to fetch today's sales");
    } finally {
      setLoading(false);
    }
  };

  const handleViewReceipt = (sale) => {
    setSelectedSale(sale);
    setShowReceiptModal(true);
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="todays-sales-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Sales Details</h1>
        </div>
        <Button
          variant="destructive"
          className="rounded-full w-12 h-12"
          onClick={() => window.history.back()}
        >
          Close
        </Button>
      </div>

      {/* Sales Table */}
      <Card>
        <CardContent className="p-0">
          {sales.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No sales today</p>
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="cart-table">
                <thead>
                  <tr>
                    <th>Sale Id</th>
                    <th>Created On</th>
                    <th>POS</th>
                    <th>Total products</th>
                    <th>Grand total</th>
                    <th>Total discount</th>
                    <th>Total tax</th>
                    <th>UserName</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id} className={sale.is_return ? "bg-red-50" : ""}>
                      <td className="font-mono">{sale.receipt_number}</td>
                      <td className="text-sm">
                        {format(new Date(sale.created_at), "dd MMMM yyyy 'at' HH:mm:ss")}
                      </td>
                      <td>Warehouse</td>
                      <td className="font-mono">
                        {sale.items.reduce((sum, item) => sum + item.quantity, 0)}
                      </td>
                      <td className={`font-mono font-bold ${sale.is_return ? "text-red-600" : ""}`}>
                        {Math.abs(sale.total).toFixed(2)}
                        {sale.is_return && " (R)"}
                      </td>
                      <td className="font-mono">{sale.discount.toFixed(2)}</td>
                      <td className="font-mono">{sale.tax.toFixed(2)}</td>
                      <td>{sale.username}</td>
                      <td>
                        <Button
                          size="sm"
                          onClick={() => handleViewReceipt(sale)}
                          className="bg-slate-500 hover:bg-slate-600 text-white"
                          data-testid={`print-receipt-${sale.id}`}
                        >
                          Print Receipt
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center">
              Receipt Preview
              <Button size="sm" onClick={handlePrintReceipt} data-testid="print-modal-receipt-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          {selectedSale && <ReceiptPreview sale={selectedSale} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
